import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  OnChanges,
  OnInit,
  SimpleChanges,
  TrackByFunction,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { concatLatestFrom } from '@ngrx/operators';
import {
  auditTime,
  combineLatest,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  map,
  Observable,
  of,
} from 'rxjs';

import { runtimeActions, runtimeSelectors } from '@blaze-runtime/engine-data-access';
import { ColumnId, PropsOf, SlotId } from '@blaze-runtime/engine-domain';
import { extractInputsFromExpressions } from '@blaze-runtime/workflow-domain';
import {
  getBackground,
  makeBorderStyles,
  makeContainerVisualEffectStyles,
  makeFontStyles,
  makeFontStyleStyles,
} from '@blaze-shared/elements-domain';
import { countedShareReplay, isDeepEqual, isNullish } from '@blaze-shared/shared-util';

import { selectPath } from '../../+state/elements';
import { CanvasElementComponent } from '../../canvas-elements/element-component';
import { checkViewportVisibility } from '../../utils/elements/element.utils';
import { CanvasElementBaseComponent } from '../__base/base.component';

import { TabItem } from './tabs.model';

export interface RuntimeTabItem extends TabItem {
  runtimeVisibility?: 'visible' | 'hidden-editor' | 'hidden';
}

@UntilDestroy()
@Component({
  selector: 'be-tabs',
  templateUrl: './tabs.component.html',
  styleUrls: ['./tabs.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TabsComponent
  extends CanvasElementBaseComponent<'tabs'>
  implements CanvasElementComponent<'tabs'>, OnChanges, OnInit
{
  @HostBinding('style.--b-tabs-internal-padding') internalPadding?: string;
  @HostBinding('style.--b-tabs-general-border-radius') generalBorderRadius?: string;
  @HostBinding('style.--b-tabs-general-background') generalBackgroundColor?: string;

  @HostBinding('style.--b-tabs-tabs-default-label') tabsDefaultLabelColor?: string;
  @HostBinding('style.--b-tabs-tabs-default-fill') tabsDefaultFill?: string;
  @HostBinding('style.--b-tabs-tabs-default-outline') tabsDefaultOutlineColor?: string;
  @HostBinding('style.--b-tabs-tabs-default-fill-hover') tabsDefaultFillHover?: string;

  @HostBinding('style.--b-tabs-tabs-selected-label') tabsSelectedLabelColor?: string;
  @HostBinding('style.--b-tabs-tabs-selected-fill') tabsSelectedFill?: string;
  @HostBinding('style.--b-tabs-tabs-selected-outline') tabsSelectedOutlineColor?: string;
  @HostBinding('style.--b-tabs-tabs-selected-fill-hover') tabsSelectedFillHover?: string;

  @HostBinding('style.--b-tabs-tabs-selected-line-label') tabsSelectedLineLabelColor?: string;
  @HostBinding('style.--b-tabs-tabs-selected-line-fill') tabsSelectedLineFill?: string;
  @HostBinding('style.--b-tabs-tabs-selected-line-outline') tabsSelectedLineOutlineColor?: string;
  @HostBinding('style.--b-tabs-tabs-selected-line-fill-hover') tabsSelectedLineFillHover?: string;

  private previousSelectedTabId?: ColumnId | undefined;

  tabStyles: Record<string, string> = {};

  selectedTabSlotId$?: Observable<ColumnId | undefined>;
  tabItems$?: Observable<RuntimeTabItem[]>;

  readonly wrapperStyles$: Observable<Record<string, string> | null>;
  readonly tabVisibility$: Observable<Record<ColumnId, boolean> | undefined> = of(undefined);

  readonly trackById: TrackByFunction<RuntimeTabItem> = (
    index: number,
    item: RuntimeTabItem,
  ): number => item.id;

  constructor() {
    super();

    this.wrapperStyles$ = this.props$.pipe(
      map((props) => ({
        ...makeBorderStyles(props?.style?.general?.outline, props?.style?.general?.outlineWidth),
        ...makeContainerVisualEffectStyles(props?.style?.general?.visualEffect),
      })),
    );

    // Update margin based on the outline or background visibility
    // undefined means - apply theme's default external spacing
    this.initMarginHandler((props) =>
      this.hasOutline(props) || this.hasBackground(props)
        ? props?.style?.general?.offset?.margin
        : '0px',
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.props) {
      this.updateStyles();
    }
  }

  ngOnInit(): void {
    // Connect selected slot to the state
    this.selectedTabSlotId$ = this.store
      .select(runtimeSelectors.selectElementMetaDataFeaturesById(this.elementId))
      .pipe(
        map((elementMeta) => {
          return elementMeta && elementMeta.elementType === 'tabs'
            ? (elementMeta.selectedSlotId as ColumnId | undefined)
            : undefined;
        }),
        distinctUntilChanged(),
        countedShareReplay(),
      );

    this.initializeTabVisibility();
    this.initializeInitialNavigation();
  }

  async showSlot(slotId?: SlotId): Promise<void> {
    if (!slotId) {
      return;
    }
    if (!this.selectedTabSlotId$) {
      return;
    }
    this.updateSelectedSlot(slotId as ColumnId);
    await firstValueFrom(
      this.selectedTabSlotId$.pipe(
        filter((id) => id === slotId),
        auditTime(0),
      ),
    );
  }

  selectTab(tab: TabItem): void {
    const slotId = tab ? (tab.id.toString() as ColumnId) : undefined;
    this.store.dispatch(selectPath({ elementId: this.elementId, slotId }));
    this.updateSelectedSlot(slotId);
  }

  hasBackground(props?: PropsOf<'tabs'>): boolean {
    return props?.style?.general?.background?.selectorType !== 'none';
  }

  hasOutline(props?: PropsOf<'tabs'>): boolean {
    return props?.style?.general?.outline.selectorType !== 'none';
  }

  private initializeTabVisibility(): void {
    this.tabItems$ = combineLatest({
      props: this.props$,
      context: this.runtimeContext$,
      viewportMode: this.viewportMode$,
    }).pipe(
      concatLatestFrom(() => this.selectedTabSlotId$ ?? of(undefined)),
      map(([{ props, context, viewportMode }, selectedSlotId]) => {
        const items = props?.content?.tabs?.items ?? [];
        const hasItemsWithVisibilityExpressions = items.some((item) => !!item.visibilityExpression);
        const normalizedContext = hasItemsWithVisibilityExpressions
          ? this.normalizeLinkedParams(context)
          : undefined;

        return { normalizedContext, viewportMode, selectedSlotId, items };
      }),
      filter(({ normalizedContext, items }) => {
        const inputs = extractInputsFromExpressions(items);
        return (
          !inputs.length ||
          inputs.every((input) => {
            const inputValue = normalizedContext?.[input];
            return (
              !isNullish(inputValue) &&
              (typeof inputValue !== 'object' || Object.keys(inputValue).length)
            );
          })
        );
      }),
      map(({ normalizedContext, viewportMode, selectedSlotId, items }) => {
        const visibleItems = items.map((item) => {
          let runtimeVisibility: RuntimeTabItem['runtimeVisibility'] = 'visible';
          if (
            !checkViewportVisibility(viewportMode, item.visible?.viewports) ||
            (item.visible?.accessRoles?.length &&
              !this.userAccessRolesService.isCurrentUserInAccessRoles(item.visible.accessRoles))
          ) {
            // Viewport visibility
            runtimeVisibility = 'hidden';
          } else if (
            item.visibilityExpression &&
            normalizedContext &&
            // Ignore normalization for better performance (the context is the same for all items)
            !this.evaluateExpression(item.visibilityExpression, normalizedContext, true)
          ) {
            // Visibility expression - partially visible in editor
            runtimeVisibility = 'hidden-editor';
            const itemId = item.id.toString();
            if (itemId === selectedSlotId) {
              this.previousSelectedTabId = itemId;
            }
          }

          return { ...item, runtimeVisibility };
        });

        // Filter out hidden items
        return visibleItems.filter((item) => {
          // In editor 'hidden-editor' items are partially visible
          if (!this.isInEditor && item.runtimeVisibility === 'hidden-editor') {
            return false;
          }
          return item.runtimeVisibility !== 'hidden';
        });
      }),
      countedShareReplay(),
    );
  }

  /**
   * Reacts to the available tab items and selects the best possible tab.
   * This gets updated when the tab visibility changes.
   * If the currently selected tab is not available, it will navigate to the first available one.
   */
  private initializeInitialNavigation(): void {
    // When the tabs items data change (visibility for example), make sure the currently selected still slot exists
    // If not -> navigate to the first one
    this.tabItems$
      ?.pipe(
        // tabItems$ should be already initialized
        distinctUntilChanged(isDeepEqual),
        concatLatestFrom(() => this.selectedTabSlotId$ ?? of(undefined)),
        map(([items, selectedSlotId]) => {
          // previously selected, but hidden due to missing inputs, tab is visible now
          const usePreviouslyFocusedTab =
            this.previousSelectedTabId !== undefined &&
            selectedSlotId !== this.previousSelectedTabId &&
            items.some(({ id }) => id.toString() === this.previousSelectedTabId);

          return { items, selectedSlotId, usePreviouslyFocusedTab };
        }),
        filter(({ items, selectedSlotId, usePreviouslyFocusedTab }) => {
          return (
            selectedSlotId === undefined ||
            !items.some((item) => item.id.toString() === selectedSlotId) ||
            usePreviouslyFocusedTab
          );
        }),
        untilDestroyed(this),
      )
      .subscribe(({ items, usePreviouslyFocusedTab }) => {
        if (usePreviouslyFocusedTab) {
          this.updateSelectedSlot(this.previousSelectedTabId);
          return;
        }

        const focusedSlotId = items
          .find((item) => item.runtimeVisibility !== 'hidden')
          ?.id.toString() as ColumnId | undefined;
        // Navigate to first visible
        this.updateSelectedSlot(focusedSlotId);
      });
  }

  private updateSelectedSlot(slotId: ColumnId | undefined): void {
    this.store.dispatch(
      runtimeActions.updateElementMeta({
        elementId: this.elementId,
        meta: {
          dataFeatures: {
            elementType: 'tabs',
            selectedSlotId: slotId,
          },
        },
      }),
    );
  }

  private updateStyles(): void {
    if (!this.props?.style) {
      // Props are not ready
      return;
    }

    const style = this.props.style;
    const { general, font, tabs } = style;
    const { default: defaultStyles, selected, selectedLine } = tabs;

    this.internalPadding = general?.offset.padding;

    const hasBackground = this.hasBackground(this.props);

    this.generalBackgroundColor =
      hasBackground && general?.background ? getBackground(general.background) : 'transparent';
    this.generalBorderRadius = general?.radius;

    // Default - all templates
    this.tabsDefaultLabelColor = defaultStyles?.label?.color1;
    this.tabsDefaultFill = defaultStyles?.fill ? getBackground(defaultStyles.fill) : 'transparent';
    this.tabsDefaultOutlineColor =
      defaultStyles?.outline?.selectorType === 'none'
        ? 'transparent'
        : defaultStyles?.outline?.color1;
    this.tabsDefaultFillHover = defaultStyles?.fillHover
      ? getBackground(defaultStyles.fillHover)
      : 'transparent';

    // Selected - most templates
    this.tabsSelectedLabelColor = selected?.label?.color1;
    this.tabsSelectedFill = selected?.fill ? getBackground(selected.fill) : 'transparent';
    this.tabsSelectedOutlineColor =
      selected?.outline?.selectorType === 'none' ? 'transparent' : selected?.outline?.color1;
    this.tabsSelectedFillHover = selected?.fillHover
      ? getBackground(selected.fillHover)
      : 'transparent';

    // Selected - line template
    this.tabsSelectedLineLabelColor = selectedLine?.label?.color1;
    this.tabsSelectedLineFill = selectedLine?.fill
      ? getBackground(selectedLine.fill)
      : 'transparent';
    this.tabsSelectedLineOutlineColor =
      selectedLine?.outline?.selectorType === 'none'
        ? 'transparent'
        : selectedLine?.outline?.color1;
    this.tabsSelectedLineFillHover = selectedLine?.fillHover
      ? getBackground(selectedLine.fillHover)
      : 'transparent';

    this.tabStyles = {
      ...makeFontStyles(font, { lineHeight: true }),
      ...makeFontStyleStyles(font?.style, '700'),
      ['font-weight']: this.fontsService.getFontWeight(font?.fontFamily, font?.fontWeight),
    };
  }
}
